import { useEffect, useState } from 'react';

import type { ToothSliceGeometryMode } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { MedicalImageRender } from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import type { FullHeadMPRToothModeState } from '../config';
import {
  readToothSliceMode,
  writeToothSliceMode,
  type ToothSliceModeScope,
} from '../lib';

type UseToothSliceModeParams = {
  isReady: boolean;
  scope: ToothSliceModeScope;
  defaultGeometryMode?: ToothSliceGeometryMode;
};

type UseToothSliceModeResult = {
  toothMode: FullHeadMPRToothModeState;
  setGeometryMode: (mode: ToothSliceGeometryMode) => void;
};

export const useToothSliceMode = (
  params: UseToothSliceModeParams,
): UseToothSliceModeResult => {
  const { isReady, scope, defaultGeometryMode } = params;

  const [toothMode, setStoredMode] = useState<FullHeadMPRToothModeState>(
    () => ({
      geometryMode: readToothSliceMode({
        fallback: defaultGeometryMode,
        scope,
      }),
      isActive: false,
      renderPath: null,
      roiStatus: 'idle',
      toothIso: null,
    }),
  );

  useEffect(() => {
    if (!isReady) {
      setStoredMode((current) => ({
        ...current,
        isActive: false,
        renderPath: null,
        roiStatus: 'idle',
        toothIso: null,
      }));
      return;
    }

    const removeListener = MedicalImageRender.addEventListener(
      'fullHeadMPRToothModeChanged',
      (event) => {
        setStoredMode({
          geometryMode: event.geometryMode,
          isActive: event.active,
          renderPath: event.renderPath,
          roiStatus: event.roiStatus,
          toothIso: event.toothIso,
        });
      },
      'fullHeadMPR',
    );

    return () => {
      removeListener?.();
    };
  }, [isReady]);

  useEffect(() => {
    if (isReady) {
      MedicalImageRender.setFullHeadMPRToothSliceMode?.(toothMode.geometryMode);
    }
  }, [isReady, toothMode.geometryMode]);

  const setGeometryMode = (mode: ToothSliceGeometryMode) => {
    writeToothSliceMode({ mode, scope });
    setStoredMode((current) => ({ ...current, geometryMode: mode }));
  };

  return { setGeometryMode, toothMode };
};
